<div class="drawer">
  <header class="drawer-header">
    <span class="drawer-title">{{ configs[currentMode].title }}</span>
    <button type="button" class="drawer-close" (click)="cancel()" aria-label="Close">×</button>
  </header>

  <div class="drawer-body">
    <div class="section-header">General</div>
    <div class="name-row">
      <app-titled-input [control]="name" [title]="'Topic name'">
        <ng-content>
          <input type="text" [formControl]="name">
        </ng-content>
      </app-titled-input>
      @if (currentMode === FormMode.EDIT && canWrite && managed) {
        <app-button text="Rename" [class]="nameChanged() ? 'primary rename-button' : 'primary rename-button disabled'" (clicked)="rename()"></app-button>
      }
    </div>
    @if (currentMode === FormMode.EDIT) {
      @if (managed) {
        <span class="field-note">Renaming keeps the collected history flowing under the new name.</span>
        @if (entity!.collecting) {
          <span class="field-note collecting">Collecting daily — a brand carrying this topic has AI Presence on.</span>
        } @else {
          <span class="field-note not-collecting"
            >Not collecting — prompts run while any brand carrying this topic has AI Presence on, including a competitor that inherited it.</span
          >
        }
      } @else {
        <span class="field-note">This topic exists only in the collection pipeline — no console topic matches it, so it does not collect. To manage it, add a topic with this exact name and attach a brand.</span>
      }

      <div class="section-header">Prompts</div>
      <!-- Adding prompts requires a console topic — the server rejects unknown topic names -->
      @if (canWrite && managed) {
        <div class="bulk-add">
          <textarea [formControl]="bulkPromptsControl" rows="4"
            placeholder="One prompt per line. To test-run a prompt before adding it, keep a single line and click “Test prompt”."></textarea>
          <div class="bulk-actions">
            @if (bulkPromptLines().length > 1) {
              <span class="field-note bulk-note">Test runs a single prompt — keep one line to test it.</span>
            }
            <app-button text="Test prompt" [class]="bulkPromptLines().length === 1 ? 'secondary' : 'secondary disabled'" (clicked)="testPrompt()"></app-button>
            <app-button [class]="promptsSaving() ? 'primary disabled' : 'primary'" text="Add prompts" (clicked)="addPrompts()"></app-button>
          </div>
        </div>
      }
      <div class="prompts-list">
        @for (prompt of prompts(); track prompt.promptId) {
          <div class="prompt-row">
            <span class="prompt-text" [title]="prompt.promptText">{{ prompt.promptText }}</span>
            <span class="prompt-date">Added {{ formatDate(prompt.createdAt) }}</span>
            @if (canDelete) {
              <app-delete-btn (clicked)="deletePrompt(prompt)"></app-delete-btn>
            }
          </div>
        } @empty {
          @if (!promptsLoading()) {
            <span class="empty-note">No prompts yet — collection for this topic starts once prompts are added.</span>
          }
        }
        @if (promptsLoading()) {
          <app-loader></app-loader>
        }
      </div>
      <div class="info-note">
        <span>Whether prompts run is controlled by the brand's <strong>AI Presence toggle</strong> (Brands tab) — pausing every attached brand stops this topic's collection and cost.</span>
        <span><strong>Delete</strong> stops a prompt running from tomorrow. Answers it already collected stay in results and reports, so past numbers do not change.</span>
      </div>

      <div class="section-header">Results</div>
      <div class="results-controls">
        <app-date-picker [range]="dateRangeGroup"></app-date-picker>
        <app-button [class]="resultsLoading() ? 'primary disabled' : 'primary'" text="Load results" (clicked)="loadResults()"></app-button>
      </div>
      <div class="results-list">
        @for (item of results(); track $index) {
          <div class="result-row" (click)="toggleExpanded($index)">
            <div class="result-summary">
              <span class="result-date">{{ formatDate(item.date) }}</span>
              <span class="result-prompt" [title]="item.promptText">{{ item.promptText }}</span>
              @if (item.mentions.length) {
                @let summary = summaryBrands(item);
                <span class="result-brands" title="Brands from the brands list that were matched in this answer">
                  @for (key of summary.shown; track key) {
                    <span class="brand-chip">{{ key }}</span>
                  }
                  @if (summary.more) {
                    <span class="brand-chip more" [title]="summary.rest">+{{ summary.more }}</span>
                  }
                </span>
              } @else {
                <span class="no-match">no brands matched</span>
              }
            </div>
            @if (isExpanded($index)) {
              <div class="result-details" (click)="$event.stopPropagation()">
                <div class="response">{{ item.responseText }}</div>
                <div class="result-actions">
                  @if (item.mentions.length) {
                    <div class="mentions">
                      @for (mention of item.mentions; track mention.brandKey) {
                        <span class="mention-chip" title="How this brand was mentioned in the answer, by sentiment">{{ mention.brandKey }} · {{ sentimentLabel(mention) }}</span>
                      }
                    </div>
                  } @else {
                    <span class="empty-note">No brands detected — a brand named in the answer but missing here should be added as a brand or alias in the Brands tab.</span>
                  }
                  <app-button text="Copy" class="secondary copy-result" (clicked)="copyResult(item)"></app-button>
                </div>
              </div>
            }
          </div>
        } @empty {
          @if (!resultsLoading()) {
            <span class="empty-note">
              @if (resultsLoaded()) {
                No results in this range. Data appears the morning after a prompt's first daily run.
              } @else {
                Pick a range and click "Load results".
              }
            </span>
          }
        }
        @if (resultsLoading()) {
          <app-loader></app-loader>
        }
      </div>
      @if (hasMoreResults) {
        <app-button text="Load more" [class]="resultsLoading() ? 'secondary load-more disabled' : 'secondary load-more'" (clicked)="loadResults(true)"></app-button>
      }
    }
  </div>

  <footer class="drawer-footer">
    @if (currentMode === FormMode.EDIT) {
      <!-- Everything in the edit drawer applies immediately (rename/add/delete have their own
           buttons), so there is nothing to save or cancel here — Close only. -->
      @if (canDelete && managed) {
        <app-button class="danger delete-topic-button" text="Delete topic" (clicked)="deleteTopic()"></app-button>
      }
      <app-button (clicked)="cancel()" class="secondary" text="Close"></app-button>
    } @else {
      <app-button (clicked)="cancel()" class="cancel" text="cancel"></app-button>
      @if (canWrite) {
        <app-button (clicked)="submit()" [class]="form.invalid || saving() ? 'primary disabled' : 'primary'" [text]="configs[currentMode].submitText"></app-button>
      }
    }
  </footer>

  @if (saving()) {
    <app-loader></app-loader>
  }
</div>
